import { Button, Center, Overlay, Text } from "@mantine/core";
import cx from "clsx";
import { type FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { CheckmarkBounce } from "@/common-ui/animations/checkmark-bounce";
import sharedStyles from "@/common-ui/sharedStyles.module.css";

import type { TranscriptContent } from "../../api/evaluations/types";

import EvaluationCallout from "../EvaluationCallout";
import classes from "../EvaluationPage.module.css";
import EvaluationTranscript from "../EvaluationTranscript";
import { EvaluationBehaviorsList } from "./EvaluationBehaviorsList";

export interface AscendEvaluationPageProps {
    onCompleteEvaluation: () => void;
    evaluationResults: TranscriptContent;
}

/**
 * ASCEND evaluation results page. Mirrors `TrainingEvaluationPage` (transcript center, feedback
 * callout right, shared `activeBehaviorIndex` driving both) but replaces the static per-partner
 * checklist on the left with `EvaluationBehaviorsList` — a dynamic list of only the behaviors present
 * in the evaluation results. Selecting a behavior points the callout at it and jumps the transcript
 * to its first `behaviorLine`, exactly as clicking a transcript highlight or the callout does today.
 */
export const AscendEvaluationPage: FC<AscendEvaluationPageProps> = ({ onCompleteEvaluation, evaluationResults }) => {
    const transcriptContainerRef = useRef<HTMLDivElement>(null);
    const [activeBehaviorIndex, setActiveBehaviorIndex] = useState(0);
    const [scrollOffset, setScrollOffset] = useState(0);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [completeEvaluation, setCompleteEvaluation] = useState(false);
    const behaviors = evaluationResults.EvaluationDetails.Body;
    const activeBehaviorLine = useMemo(() => {
        if (behaviors[activeBehaviorIndex]?.behaviorLine?.length) {
            return behaviors[activeBehaviorIndex].behaviorLine[0];
        } else {
            return undefined;
        }
    }, [behaviors, activeBehaviorIndex]);

    // Pagination reflects position within the flat results list (1-based) — not the per-category
    // checklist position that `getChecklistCategoryCount` returns — so it starts at "1 of N" and
    // advances one-for-one as the expert steps through the behaviors.
    const pagination = useMemo(
        () => ({ currentIndex: activeBehaviorIndex + 1, total: behaviors.length }),
        [activeBehaviorIndex, behaviors.length],
    );

    useEffect(() => {
        if (completeEvaluation) {
            onCompleteEvaluation();
        }
    }, [completeEvaluation, onCompleteEvaluation]);

    useLayoutEffect(() => {
        if (transcriptContainerRef?.current) {
            const offset = transcriptContainerRef.current.scrollHeight / 2 - 100;
            setScrollOffset(offset);
        }
    }, []);

    return (
        <div className={classes.background}>
            <div className={classes.container}>
                {overlayVisible && (
                    <Overlay data-testid="return-home-screen" color="var(--exwo-color-app-bg)" backgroundOpacity={0.85}>
                        <Center
                            style={{ flexDirection: "column", height: "100%", gap: "1.5rem" }}
                            className={sharedStyles.animateScaleIn}
                        >
                            <CheckmarkBounce />
                            <Text fw="700" ta="center" size="xl">
                                Thank you for reviewing!
                            </Text>
                            <Button
                                onClick={() => {
                                    setCompleteEvaluation(true);
                                }}
                            >
                                Return Home
                            </Button>
                        </Center>
                    </Overlay>
                )}
                <div className={classes.evaluationGrid}>
                    <div className={cx(classes.evaluationGridColumn, classes.sidebar)}>
                        <EvaluationBehaviorsList
                            behaviors={behaviors}
                            activeBehaviorIndex={activeBehaviorIndex}
                            onClickBehavior={setActiveBehaviorIndex}
                        />
                    </div>
                    <div className={cx(classes.evaluationGridColumn, classes.content)} ref={transcriptContainerRef}>
                        <EvaluationTranscript
                            activeBehaviorLine={activeBehaviorLine}
                            behaviors={behaviors}
                            evaluationResults={evaluationResults}
                            onClickHighlight={(transcriptBehavior) => {
                                if (transcriptBehavior) {
                                    const newBehaviorIndex = behaviors.findIndex(
                                        ({ behavior }) => behavior === transcriptBehavior,
                                    );
                                    const index = newBehaviorIndex >= 0 ? newBehaviorIndex : 0;
                                    setActiveBehaviorIndex(index);
                                } else {
                                    setActiveBehaviorIndex(0);
                                }
                            }}
                            scrollOffset={scrollOffset}
                        />
                    </div>
                    <div className={cx(classes.evaluationGridColumn, classes.calloutCard)}>
                        <EvaluationCallout
                            key={behaviors[activeBehaviorIndex]?.behavior ?? `behavior-${activeBehaviorIndex}`}
                            activeBehavior={behaviors[activeBehaviorIndex]}
                            onClickOk={() => {
                                const nextBehavior = activeBehaviorIndex + 1;

                                if (nextBehavior >= behaviors.length) {
                                    setOverlayVisible(true);
                                } else {
                                    setActiveBehaviorIndex(nextBehavior);
                                }
                            }}
                            pagination={
                                behaviors.length > 0 ? (
                                    <Text size="xxs" style={{ textWrap: "nowrap" }}>
                                        {pagination.currentIndex} of {pagination.total}
                                    </Text>
                                ) : null
                            }
                            scrollOffset={scrollOffset}
                            transcriptLength={evaluationResults?.Transcriptions.length ?? 0}
                            trainingSessionId={evaluationResults?.SessionID}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AscendEvaluationPage;
